/**
 * Instrument classification and party-name splitting.
 *
 * The two helpers the title chart needs, lifted from Shadowland Title's
 * larger ownership engine with the rest of that engine left behind —
 * this app has its own ownership pipeline and does not need a second one.
 *
 * What it does need is the chart's reading of an instrument: whether a
 * recorded document is a conveyance, a lease, a release, a probate paper
 * or an encumbrance decides which boxes and arrows get drawn, and getting
 * it wrong points an arrow the wrong way down the chain.
 */
const CONVEYANCE = [
  'warranty deed', 'special warranty deed', 'limited warranty deed', 'general warranty deed',
  'quitclaim deed', 'quit claim deed', 'mineral deed', 'royalty deed', 'gift deed',
  'correction deed', 'correction mineral deed', 'deed', 'patent', 'sheriff\'s deed',
  'tax deed', 'trustee\'s deed', 'executor\'s deed', 'administrator\'s deed',
  'personal representative\'s deed', 'distribution deed', 'partition deed',
  'exchange deed', 'assignment', 'partial assignment', 'conveyance',
];

const LEASE = ['oil and gas lease', 'oil gas and mineral lease', 'lease', 'memorandum of lease'];

const ENCUMBRANCE = [
  'mortgage', 'deed of trust', 'easement', 'right of way', 'right-of-way',
  'pipeline easement', 'surface use agreement', 'lien', 'judgment', 'restrictive covenant',
];

const RELEASE = ['release', 'partial release', 'surrender', 'satisfaction', 'reconveyance'];

const PROBATE = [
  'affidavit of heirship', 'determination of heirship', 'last will and testament',
  'order admitting will to probate', 'probate', 'letters testamentary', 'affidavit',
  'death certificate', 'date of death',
];

/** Corporate suffixes, which follow a comma without starting a new party. */
const ENTITY_SUFFIX = /,\s*(?=(?:inc|incorporated|llc|l\.l\.c|llp|lp|plc|pllc|corp|corporation|co|company|ltd|limited|n\.a|na|fsb|trustee|trustees|jr|sr|ii|iii|iv)\b\.?)/gi;

/**
 * Capacity and relationship phrases, which describe a party without being one.
 *
 * These are stripped before anything is split, because most of them contain
 * their own "and" — "husband and wife" is the commonest text in a county deed
 * index — and splitting on that first turns one couple into the words
 * "husband" and "wife".
 */
const CAPACITY = new RegExp(
  ',?\\s*\\b(?:'
  + 'husband and wife|wife and husband|man and wife'
  + '|(?:a|an)\\s+(?:single|married|unmarried|widowed)\\s+(?:man|woman|person)'
  + '|(?:a|an)\\s+(?:widow|widower|single person)'
  + '|dealing in (?:his|her|their) sole and separate property'
  + '|individually and as trustees?|individually'
  + '|as (?:his|her|their) sole and separate property'
  + '|joint tenants(?: with right of survivorship)?|tenants in common'
  + '|(?:his|her) (?:wife|husband)'
  + '|deceased|dec\'?d'
  + '|et\\.?\\s?(?:al|ux|vir)\\.?'
  + ')\\b\\.?',
  'gi',
);

/**
 * Words that sit either side of an "and" inside one company's name.
 *
 * "Quail Land and Cattle Co" and "Smith Brothers Oil and Gas, Inc." are one
 * party each, not two, and splitting them produces a conveyance between two
 * halves of the same company. There is no way to tell that apart from "John
 * Doe and Acme LLC" by shape alone — both are a noun phrase either side of an
 * "and" — so this is a list of the words that actually appear in the middle of
 * company names in county records. Anything not on it splits, which is the
 * safer default: a party wrongly split shows as two boxes an agent can merge,
 * while a party wrongly joined hides a grantor entirely.
 */
const COMPANY_JOINERS = new RegExp(
  '^(?:oil|gas|land|cattle|ranch|ranches|loan|savings|title|abstract|development'
  + '|exploration|production|royalty|royalties|mineral|minerals|livestock|farm'
  + '|farms|grain|supply|equipment|investment|investments|realty|improvement'
  + '|irrigation|power|light|water|telegraph|telephone|railway|railroad|brothers'
  + '|bros|sons|guaranty|casualty|indemnity|surety)$',
  'i',
);

/** What kind of instrument this row is, for ownership purposes. */
export function classifyInstrument(instrument) {
  const text = String(instrument || '').toLowerCase().trim();
  if (!text) return 'unknown';
  const has = (list) => list.some((term) => text.includes(term));
  // Order matters: "memorandum of lease" is a lease, "release" is not a lease
  // even though it contains "lease" as a substring — check release first.
  if (has(RELEASE)) return 'release';
  if (has(PROBATE)) return 'probate';
  if (has(ENCUMBRANCE)) return 'encumbrance';
  if (has(LEASE)) return 'lease';
  if (has(CONVEYANCE)) return 'conveyance';
  return 'other';
}

/**
 * "A; B and C" -> ["A", "B", "C"], preserving display names.
 *
 * County party text is written for a human reading one deed, not for parsing:
 * it is often entirely upper case, it carries capacity phrases the chain does
 * not care about, and its separators are inconsistent within a single index.
 * Every grantor named has to come out as its own name, because on the chart
 * each of them gets a box and each of them goes on to convey separately.
 */
export function splitPartyNames(value) {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(CAPACITY, '')
    // ", Inc." is a suffix, not the start of a second party. Joined before the
    // comma split so an all-caps "SMITH RANCH, INC." stays one name.
    .replace(ENTITY_SUFFIX, ' ');

  // Commas and semicolons separate parties outright. The comma no longer needs
  // to look ahead for a capitalised word — which never matched the upper-case
  // text half the county writes in — because the suffixes that follow a comma
  // without starting a party have already been joined up.
  const parts = [];
  for (const chunk of text.split(/[;,]|\s*&\s*/)) {
    // "and" splits only where it is not sitting inside one company's name.
    // The connector is captured rather than discarded so a name that turns out
    // to be one company reads back as it was written — an all-caps index entry
    // must not come out of here as "SMITH BROTHERS OIL and GAS".
    const pieces = chunk.split(/\s+(\band\b)\s+/i);
    let held = '';
    for (let i = 0; i < pieces.length; i += 2) {
      const piece = pieces[i];
      const connector = pieces[i - 1] || 'and';
      const tail = held.split(/\s+/).pop() || '';
      const head = piece.split(/\s+/)[0] || '';
      if (held && (COMPANY_JOINERS.test(tail) || COMPANY_JOINERS.test(head))) {
        held = `${held} ${connector} ${piece}`;
        continue;
      }
      if (held) parts.push(held);
      held = piece;
    }
    if (held) parts.push(held);
  }

  return parts
    .map((part) => part.trim().replace(/[.,;\s]+$/, '').trim())
    .filter((part) => part.length > 1
      && !/^(?:et\s?al|et\s?ux|et\s?vir|husband|wife|jr|sr|the|and|trustees?)\.?$/i.test(part));
}
